import { useLocation, useNavigate } from 'react-router-dom';
import { ClientAvatar } from './ClientAvatar';
import { SCAFreshnessChip } from '@/components/SCAFreshnessChip';
import { CUSTOMER_SCA_KEY } from '@/hooks/useSCAFreshness';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { NotificationCenter } from './NotificationCenter';
import { useUnreadChatCount } from '@/hooks/useUnreadChatCount';
import paynovusLogo from '@/assets/paynovus-logo-horizontal.svg';
import {
  LayoutDashboard, Wallet, Send, Users, ArrowLeftRight,
  CreditCard, Crown, Settings, HelpCircle, LogOut, ChevronRight, Briefcase, Building2, ArrowRightLeft, MessageSquare, MessageCircle,
  Layers, Receipt, ArrowDownLeft, Inbox, BookOpen, Plug, UserPlus,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const mainNav: { label: string; icon: any; path: string; comingSoon?: boolean }[] = [
  { label: 'Overview', icon: LayoutDashboard, path: '/app' },
  { label: 'Accounts', icon: Wallet, path: '/app/accounts' },
  { label: 'Statements', icon: Receipt, path: '/app/statements' },
  { label: 'Payments', icon: Send, path: '/app/payments' },
  { label: 'Beneficiaries', icon: Users, path: '/app/beneficiaries' },
  { label: 'FX Exchange', icon: ArrowLeftRight, path: '/app/exchange' },
  { label: 'Cards', icon: CreditCard, path: '/app/cards' },
  { label: 'Messages', icon: MessageCircle, path: '/app/chat' },
];

const secondaryNav = [
  { label: 'Help & Support', icon: MessageSquare, path: '/app/support' },
  { label: 'Plan & Benefits', icon: Crown, path: '/app/plan' },
  { label: 'Settings', icon: Settings, path: '/app/settings' },
];

export function CustomerSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut, availableContexts, switchProfile, user } = useCustomerAuth();
  const isCorporate = profile?.client_type === 'corporate';
  const unreadChatCount = useUnreadChatCount();
  const hasMultipleProfiles = availableContexts.length > 1;
  const hasPersonalAccount = availableContexts.some(ctx => ctx.client_type === 'individual');

  // Check if user has card program entitlement (team member or PM owner)
  const { data: hasProgramAccess } = useQuery({
    queryKey: ['program-access-check', user?.id, profile?.id],
    enabled: !!user?.id && isCorporate,
    queryFn: async () => {
      // Check team membership
      const { data: tm } = await (supabase as any)
        .from('program_team_members')
        .select('id')
        .eq('user_id', user!.id)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();
      if (tm) return true;

      // Check PM ownership
      if (profile?.id) {
        const { data: pm } = await (supabase as any)
          .from('program_managers')
          .select('id')
          .eq('client_id', profile.id)
          .eq('status', 'active')
          .limit(1)
          .maybeSingle();
        if (pm) return true;
      }
      return false;
    },
  });

  const isActive = (path: string) =>
    location.pathname === path || (path !== '/app' && location.pathname.startsWith(path));

  return (
    <aside className="w-[280px] bg-card/50 backdrop-blur-sm border-r border-border flex flex-col h-screen sticky top-0">
      {/* Logo & Brand */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <img src={paynovusLogo} alt="PayNovus" className="h-8 w-auto" />
          <NotificationCenter />
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <p className="px-3 pt-3 pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Banking</p>
        {mainNav.map((item) => {
          const active = isActive(item.path);
          const navBtn = (
            <button
              key={item.path}
              onClick={() => !item.comingSoon && navigate(item.path)}
              disabled={item.comingSoon}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                item.comingSoon
                  ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                  : active
                    ? 'bg-accent/15 text-accent shadow-sm shadow-accent/5'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                active ? 'bg-accent/20' : 'bg-transparent group-hover:bg-secondary'
              }`}>
                <item.icon className="w-[18px] h-[18px]" />
              </div>
              <span className="flex-1 text-left">{item.label}</span>
              {item.path === '/app/chat' && unreadChatCount > 0 && !active && (
                <span className="w-5 h-5 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center shrink-0">
                  {unreadChatCount > 9 ? '9+' : unreadChatCount}
                </span>
              )}
              {item.comingSoon && (
                <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                  Soon
                </span>
              )}
              {active && !item.comingSoon && <ChevronRight className="w-4 h-4 opacity-50" />}
            </button>
          );

          if (item.comingSoon) {
            return (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>{navBtn}</TooltipTrigger>
                <TooltipContent side="right">Coming soon</TooltipContent>
              </Tooltip>
            );
          }
          return navBtn;
        })}

        {/* Corporate-only: Team Management + Bookkeeping */}
        {isCorporate && (
          <>
            <div className="my-3 mx-3 border-t border-border/50" />
            <p className="px-3 pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Corporate</p>
            {[
        { label: 'Team Management', icon: Users, path: '/app/team' },
              { label: 'Bookkeeping', icon: BookOpen, path: '/app/bookkeeping' },
              { label: 'Integrations', icon: Plug, path: '/app/integrations' },
            ].map(item => {
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                    active ? 'bg-blue-500/15 text-blue-400 shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                    active ? 'bg-blue-500/20' : 'bg-transparent group-hover:bg-secondary'
                  }`}>
                    <item.icon className="w-[18px] h-[18px]" />
                  </div>
                  <span className="flex-1 text-left">{item.label}</span>
                  {active && <ChevronRight className="w-4 h-4 opacity-50" />}
                </button>
              );
            })}
          </>
        )}

        {/* Card Program Portal — only for entitled users */}
        {hasProgramAccess && (
          <>
            <div className="my-3 mx-3 border-t border-border/50" />
            <p className="px-3 pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Card Program</p>
            {(() => {
              const active = isActive('/app/program');
              return (
                <button
                  onClick={() => navigate('/app/program')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                    active ? 'bg-emerald-500/15 text-emerald-400 shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                    active ? 'bg-emerald-500/20' : 'bg-transparent group-hover:bg-secondary'
                  }`}>
                    <Layers className="w-[18px] h-[18px]" />
                  </div>
                  <span className="flex-1 text-left">Card Program</span>
                  {active && <ChevronRight className="w-4 h-4 opacity-50" />}
                </button>
              );
            })()}
          </>
        )}

        <div className="my-3 mx-3 border-t border-border/50" />
        
        <p className="px-3 pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Support</p>
        {secondaryNav.map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                active
                  ? 'bg-accent/15 text-accent'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
              }`}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                <item.icon className="w-[18px] h-[18px]" />
              </div>
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* User Footer */}
      <div className="p-4 border-t border-border/50 space-y-2">
        <SCAFreshnessChip storageKey={CUSTOMER_SCA_KEY} className="w-full justify-center" />
        <div className="flex items-center gap-3 px-2 mb-1">
          <div className="w-10 h-10">
            {isCorporate ? (
              <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm border bg-gradient-to-br from-blue-500/30 to-blue-400/10 text-blue-400 border-blue-500/20">
                <Building2 className="w-5 h-5" />
              </div>
            ) : (
              <ClientAvatar size="md" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              {isCorporate ? (profile?.company_name || 'Business') : (profile?.full_name || 'User')}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {isCorporate ? 'Business Account' : 'Personal Account'}
            </p>
          </div>
        </div>

        {/* Profile Switcher */}
        {hasMultipleProfiles && (
          <div className="space-y-1">
            {availableContexts
              .filter(ctx => ctx.client_id !== profile?.id)
              .map(ctx => (
                <button
                  key={ctx.client_id}
                  onClick={async () => {
                    await switchProfile(ctx.client_id);
                    navigate('/app');
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-all duration-200"
                >
                  <ArrowRightLeft className="w-3.5 h-3.5" />
                  <span className="truncate">Switch to {ctx.label}</span>
                  {ctx.client_type === 'corporate' && (
                    <span className="text-[8px] font-bold uppercase px-1 py-0.5 rounded bg-blue-500/15 text-blue-400 ml-auto shrink-0">BIZ</span>
                  )}
                </button>
              ))}
          </div>
        )}

        {/* Open Personal Account — only shown for users without a personal profile */}
        {!hasPersonalAccount && (
          <button
            onClick={() => navigate('/app/onboard?upgrade=personal')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium text-accent hover:bg-accent/10 transition-all duration-200"
          >
            <UserPlus className="w-3.5 h-3.5" />
            <span>Open Personal Account</span>
          </button>
        )}

        <button
          onClick={async () => { await signOut(); navigate('/app/login'); }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </aside>
  );
}
